/**
 * Storage utilities for localStorage and sessionStorage operations
 * Provides safe access with error handling and namespacing
 */

const STORAGE_PREFIX = 'fyncall_';

/**
 * Storage keys used by the widget
 */
export const STORAGE_KEYS = {
    VISITOR_ID: 'visitor_id',
    SESSION_ID: 'session_id',
    VISITOR_TOKEN: 'visitor_token',
    VISITOR_INFO: 'visitor_info',
    THEME: 'theme',
    SOUND_ENABLED: 'sound_enabled',
    VISIT_COUNT: 'visit_count',
    LAST_VISIT: 'last_visit',
    PROACTIVE_STATE: 'proactive_state',
    MESSAGES: 'messages',  // Message cache prefix (per session)
    LAST_ACTIVITY: 'last_activity',  // Timestamp of last user activity
    LAST_SYNC: 'last_sync'  // Timestamp of last server sync
};

/**
 * Session TTL: 24 hours of inactivity before session expires
 */
export const SESSION_TTL = 24 * 60 * 60 * 1000;

/**
 * Get the full storage key with prefix
 * @param {string} key - Key name
 * @returns {string} Prefixed key
 */
function getKey(key) {
    return STORAGE_PREFIX + key;
}

/**
 * Safely get an item from localStorage
 * @param {string} key - Storage key (without prefix)
 * @param {*} defaultValue - Default value if not found or error
 * @returns {*} Stored value or default
 */
export function getLocalItem(key, defaultValue = null) {
    try {
        const value = localStorage.getItem(getKey(key));
        return value !== null ? value : defaultValue;
    } catch (e) {
        console.warn('[FyncallStorage] Error reading localStorage:', e);
        return defaultValue;
    }
}

/**
 * Safely set an item in localStorage
 * @param {string} key - Storage key (without prefix)
 * @param {string} value - Value to store
 * @returns {boolean} Success status
 */
export function setLocalItem(key, value) {
    try {
        localStorage.setItem(getKey(key), value);
        return true;
    } catch (e) {
        console.warn('[FyncallStorage] Error writing localStorage:', e);
        return false;
    }
}

/**
 * Safely remove an item from localStorage
 * @param {string} key - Storage key (without prefix)
 * @returns {boolean} Success status
 */
export function removeLocalItem(key) {
    try {
        localStorage.removeItem(getKey(key));
        return true;
    } catch (e) {
        console.warn('[FyncallStorage] Error removing from localStorage:', e);
        return false;
    }
}

/**
 * Get a JSON-parsed item from localStorage
 * @param {string} key - Storage key (without prefix)
 * @param {*} defaultValue - Default value if not found or parse error
 * @returns {*} Parsed value or default
 */
export function getLocalJSON(key, defaultValue = null) {
    try {
        const value = localStorage.getItem(getKey(key));
        if (value === null) return defaultValue;
        return JSON.parse(value);
    } catch (e) {
        console.warn('[FyncallStorage] Error parsing JSON from localStorage:', e);
        return defaultValue;
    }
}

/**
 * Set a JSON-stringified item in localStorage
 * @param {string} key - Storage key (without prefix)
 * @param {*} value - Value to store (will be JSON.stringified)
 * @returns {boolean} Success status
 */
export function setLocalJSON(key, value) {
    try {
        localStorage.setItem(getKey(key), JSON.stringify(value));
        return true;
    } catch (e) {
        console.warn('[FyncallStorage] Error writing JSON to localStorage:', e);
        return false;
    }
}

/**
 * Safely get an item from sessionStorage
 * @param {string} key - Storage key (without prefix)
 * @param {*} defaultValue - Default value if not found or error
 * @returns {*} Stored value or default
 */
export function getSessionItem(key, defaultValue = null) {
    try {
        const value = sessionStorage.getItem(getKey(key));
        return value !== null ? value : defaultValue;
    } catch (e) {
        console.warn('[FyncallStorage] Error reading sessionStorage:', e);
        return defaultValue;
    }
}

/**
 * Safely set an item in sessionStorage
 * @param {string} key - Storage key (without prefix)
 * @param {string} value - Value to store
 * @returns {boolean} Success status
 */
export function setSessionItem(key, value) {
    try {
        sessionStorage.setItem(getKey(key), value);
        return true;
    } catch (e) {
        console.warn('[FyncallStorage] Error writing sessionStorage:', e);
        return false;
    }
}

/**
 * Safely remove an item from sessionStorage
 * @param {string} key - Storage key (without prefix)
 * @returns {boolean} Success status
 */
export function removeSessionItem(key) {
    try {
        sessionStorage.removeItem(getKey(key));
        return true;
    } catch (e) {
        console.warn('[FyncallStorage] Error removing from sessionStorage:', e);
        return false;
    }
}

/**
 * Get a JSON-parsed item from sessionStorage
 * @param {string} key - Storage key (without prefix)
 * @param {*} defaultValue - Default value if not found or parse error
 * @returns {*} Parsed value or default
 */
export function getSessionJSON(key, defaultValue = null) {
    try {
        const value = sessionStorage.getItem(getKey(key));
        if (value === null) return defaultValue;
        return JSON.parse(value);
    } catch (e) {
        console.warn('[FyncallStorage] Error parsing JSON from sessionStorage:', e);
        return defaultValue;
    }
}

/**
 * Set a JSON-stringified item in sessionStorage
 * @param {string} key - Storage key (without prefix)
 * @param {*} value - Value to store (will be JSON.stringified)
 * @returns {boolean} Success status
 */
export function setSessionJSON(key, value) {
    try {
        sessionStorage.setItem(getKey(key), JSON.stringify(value));
        return true;
    } catch (e) {
        console.warn('[FyncallStorage] Error writing JSON to sessionStorage:', e);
        return false;
    }
}

/**
 * Clear all Fyncall-related items from localStorage
 */
export function clearLocalStorage() {
    try {
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith(STORAGE_PREFIX)) {
                localStorage.removeItem(key);
            }
        });
    } catch (e) {
        console.warn('[FyncallStorage] Error clearing localStorage:', e);
    }
}

/**
 * Clear all Fyncall-related items from sessionStorage
 */
export function clearSessionStorage() {
    try {
        Object.keys(sessionStorage).forEach(key => {
            if (key.startsWith(STORAGE_PREFIX)) {
                sessionStorage.removeItem(key);
            }
        });
    } catch (e) {
        console.warn('[FyncallStorage] Error clearing sessionStorage:', e);
    }
}

// Session-specific helpers

/**
 * Get or generate a visitor ID
 * @returns {string} Visitor ID
 */
export function getOrCreateVisitorId() {
    let visitorId = getLocalItem(STORAGE_KEYS.VISITOR_ID);
    if (!visitorId) {
        visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
        setLocalItem(STORAGE_KEYS.VISITOR_ID, visitorId);
    }
    return visitorId;
}

/**
 * Save session data to localStorage
 * @param {string} sessionId - Session ID
 * @param {string} visitorToken - Visitor authentication token
 * @returns {boolean} Success status - false if invalid values provided
 */
export function saveSession(sessionId, visitorToken) {
    // Validate inputs - don't save null/undefined values
    if (!sessionId || !visitorToken ||
        sessionId === 'null' || sessionId === 'undefined' ||
        visitorToken === 'null' || visitorToken === 'undefined') {
        console.warn('[FyncallStorage] Attempted to save invalid session data:', { sessionId, visitorToken });
        return false;
    }
    setLocalItem(STORAGE_KEYS.SESSION_ID, sessionId.toString());
    setLocalItem(STORAGE_KEYS.VISITOR_TOKEN, visitorToken);
    // Update activity timestamp when session is created/saved
    setLocalItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
    return true;
}

/**
 * Check if a value is a valid session value (not null, "null", undefined, "undefined", or empty)
 * @param {*} value - Value to check
 * @returns {string|null} The value if valid, null otherwise
 */
function validateSessionValue(value) {
    if (!value || value === 'null' || value === 'undefined' || value === '') {
        return null;
    }
    return value;
}

/**
 * Get saved session data from localStorage
 * @returns {{ sessionId: string, visitorToken: string } | null} Session data or null if invalid
 */
export function getSavedSession() {
    const sessionId = validateSessionValue(getLocalItem(STORAGE_KEYS.SESSION_ID));
    const visitorToken = validateSessionValue(getLocalItem(STORAGE_KEYS.VISITOR_TOKEN));

    // If either value is invalid, clear both and return null
    if (!sessionId || !visitorToken) {
        clearSavedSession();
        return null;
    }

    return { sessionId, visitorToken };
}

/**
 * Clear saved session data
 */
export function clearSavedSession() {
    removeLocalItem(STORAGE_KEYS.SESSION_ID);
    removeLocalItem(STORAGE_KEYS.VISITOR_TOKEN);
    removeLocalItem(STORAGE_KEYS.LAST_ACTIVITY);
    removeLocalItem(STORAGE_KEYS.LAST_SYNC);
}

/**
 * Get and update visit count
 * @returns {{ count: number, isNewVisit: boolean }} Visit info
 */
export function trackVisit() {
    let visitCount = parseInt(getLocalItem(STORAGE_KEYS.VISIT_COUNT, '0'));
    const lastVisit = getLocalItem(STORAGE_KEYS.LAST_VISIT);
    const now = Date.now();

    // Consider it a new visit if more than 30 minutes since last visit
    const isNewVisit = !lastVisit || (now - parseInt(lastVisit)) > 30 * 60 * 1000;

    if (isNewVisit) {
        visitCount++;
        setLocalItem(STORAGE_KEYS.VISIT_COUNT, visitCount.toString());
        setLocalItem(STORAGE_KEYS.LAST_VISIT, now.toString());
    }

    return { count: visitCount, isNewVisit };
}

/**
 * Get current visit count
 * @returns {number} Visit count
 */
export function getVisitCount() {
    return parseInt(getLocalItem(STORAGE_KEYS.VISIT_COUNT, '0'));
}

/**
 * Save visitor info from pre-chat form
 * @param {Object} info - Visitor info object
 */
export function saveVisitorInfo(info) {
    setLocalJSON(STORAGE_KEYS.VISITOR_INFO, info);
}

/**
 * Get saved visitor info
 * @returns {Object|null} Visitor info or null
 */
export function getVisitorInfo() {
    return getLocalJSON(STORAGE_KEYS.VISITOR_INFO);
}

/**
 * Save proactive engagement state to sessionStorage
 * @param {Object} state - Proactive state object
 */
export function saveProactiveState(state) {
    setSessionJSON(STORAGE_KEYS.PROACTIVE_STATE, {
        triggersShown: state.triggersShown,
        triggerCount: state.triggerCount,
        lastTriggerTime: state.lastTriggerTime
    });
}

/**
 * Load proactive engagement state from sessionStorage
 * @returns {Object|null} Proactive state or null
 */
export function loadProactiveState() {
    return getSessionJSON(STORAGE_KEYS.PROACTIVE_STATE);
}

/**
 * Get saved theme preference
 * @returns {string|null} Theme name or null
 */
export function getSavedTheme() {
    return getLocalItem(STORAGE_KEYS.THEME);
}

/**
 * Save theme preference
 * @param {string} theme - Theme name ('light' or 'dark')
 */
export function saveTheme(theme) {
    setLocalItem(STORAGE_KEYS.THEME, theme);
}

/**
 * Get sound enabled preference
 * @returns {boolean} Sound enabled status
 */
export function getSoundEnabled() {
    const value = getLocalItem(STORAGE_KEYS.SOUND_ENABLED);
    return value === null ? true : value === 'true';
}

/**
 * Save sound enabled preference
 * @param {boolean} enabled - Sound enabled status
 */
export function saveSoundEnabled(enabled) {
    setLocalItem(STORAGE_KEYS.SOUND_ENABLED, enabled.toString());
}

// =====================================================================
// SESSION EXPIRY MANAGEMENT
// =====================================================================

/**
 * Check if the current session has expired
 * Session expires after 24 hours of inactivity
 * @returns {boolean} True if session is expired
 */
export function isSessionExpired() {
    const lastActivity = getLocalItem(STORAGE_KEYS.LAST_ACTIVITY);
    if (!lastActivity) {
        // No activity recorded - consider it as needing a fresh session
        return true;
    }

    const lastActivityTime = parseInt(lastActivity, 10);
    if (isNaN(lastActivityTime)) {
        return true;
    }

    const timeSinceActivity = Date.now() - lastActivityTime;
    return timeSinceActivity > SESSION_TTL;
}

/**
 * Update the last activity timestamp
 * Should be called on user interactions (send message, receive message, etc.)
 */
export function updateSessionActivity() {
    setLocalItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
}

/**
 * Get the time since last activity in milliseconds
 * @returns {number} Time since last activity in ms, or Infinity if no activity recorded
 */
export function getSessionAge() {
    const lastActivity = getLocalItem(STORAGE_KEYS.LAST_ACTIVITY);
    if (!lastActivity) {
        return Infinity;
    }

    const lastActivityTime = parseInt(lastActivity, 10);
    if (isNaN(lastActivityTime)) {
        return Infinity;
    }

    return Date.now() - lastActivityTime;
}

/**
 * Clear session activity timestamp
 * Called when session is cleared
 */
export function clearSessionActivity() {
    removeLocalItem(STORAGE_KEYS.LAST_ACTIVITY);
    removeLocalItem(STORAGE_KEYS.LAST_SYNC);
}

/**
 * Update the last sync timestamp
 * Called after successfully syncing with server
 */
export function updateLastSync() {
    setLocalItem(STORAGE_KEYS.LAST_SYNC, Date.now().toString());
}

/**
 * Get the last sync timestamp
 * @returns {number|null} Last sync timestamp or null
 */
export function getLastSync() {
    const lastSync = getLocalItem(STORAGE_KEYS.LAST_SYNC);
    if (!lastSync) return null;
    const timestamp = parseInt(lastSync, 10);
    return isNaN(timestamp) ? null : timestamp;
}

// =====================================================================
// MESSAGE CACHING
// =====================================================================

/**
 * Get the cache key for a session's messages
 * @param {string} sessionId - Session ID
 * @returns {string} Cache key
 */
function getMessageCacheKey(sessionId) {
    return `${STORAGE_PREFIX}messages_${sessionId}`;
}

/**
 * Save messages to localStorage cache
 * @param {string} sessionId - Session ID
 * @param {Array} messages - Array of message objects
 */
export function cacheMessages(sessionId, messages) {
    if (!sessionId || !messages) return;

    const cacheKey = getMessageCacheKey(sessionId);
    const cacheData = {
        messages: messages,
        timestamp: Date.now(),
        sessionId: sessionId
    };

    try {
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (e) {
        // Handle quota exceeded - clear old caches
        clearOldMessageCaches();
        try {
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        } catch (e2) {
            console.warn('[FyncallStorage] Failed to cache messages:', e2);
        }
    }
}

/**
 * Get cached messages for a session
 * @param {string} sessionId - Session ID
 * @returns {Array|null} Cached messages or null
 */
export function getCachedMessages(sessionId) {
    if (!sessionId) return null;

    const cacheKey = getMessageCacheKey(sessionId);
    try {
        const cached = localStorage.getItem(cacheKey);
        if (!cached) return null;

        const data = JSON.parse(cached);
        // Invalidate cache after 24 hours
        const maxAge = 24 * 60 * 60 * 1000;
        if (Date.now() - data.timestamp > maxAge) {
            localStorage.removeItem(cacheKey);
            return null;
        }

        return data.messages;
    } catch (e) {
        console.warn('[FyncallStorage] Failed to read message cache:', e);
        return null;
    }
}

/**
 * Append a single message to the cache
 * @param {string} sessionId - Session ID
 * @param {Object} message - Message object
 */
export function appendMessageToCache(sessionId, message) {
    if (!sessionId || !message) return;

    const cached = getCachedMessages(sessionId) || [];

    // Avoid duplicates by checking message ID
    if (message.id && cached.some(m => m.id === message.id)) {
        return;
    }

    cached.push(message);
    cacheMessages(sessionId, cached);
}

/**
 * Clear message cache for a session
 * @param {string} sessionId - Session ID
 */
export function clearMessageCache(sessionId) {
    if (!sessionId) return;
    const cacheKey = getMessageCacheKey(sessionId);
    try {
        localStorage.removeItem(cacheKey);
    } catch (e) {
        console.warn('[FyncallStorage] Failed to clear message cache:', e);
    }
}

/**
 * Clear old message caches to free up space
 * Keeps only the 5 most recent sessions
 */
function clearOldMessageCaches() {
    const prefix = `${STORAGE_PREFIX}messages_`;
    const cacheEntries = [];

    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(prefix)) {
                try {
                    const data = JSON.parse(localStorage.getItem(key));
                    cacheEntries.push({ key, timestamp: data.timestamp || 0 });
                } catch (e) {
                    // Invalid cache entry, remove it
                    localStorage.removeItem(key);
                }
            }
        }

        // Sort by timestamp (oldest first) and remove all but 5 most recent
        if (cacheEntries.length > 5) {
            cacheEntries
                .sort((a, b) => a.timestamp - b.timestamp)
                .slice(0, cacheEntries.length - 5)
                .forEach(entry => {
                    localStorage.removeItem(entry.key);
                });
        }
    } catch (e) {
        console.warn('[FyncallStorage] Failed to clear old message caches:', e);
    }
}
